import ICommand from '../../FrontApplet/Command/ICommand';
import { ITypedCommandEvent } from '../../FrontApplet/Command/ICommandEvent';
import FrontApplet from '../../FrontApplet/FrontApplet';
import {
	FileSystemAppendFileRequest,
	FileSystemAppendFileResult,
	FileSystemCopyFileRequest,
	FileSystemCopyFileResult,
	FileSystemCreateArchiveFileRequest,
	FileSystemCreateArchiveFileResult,
	FileSystemCreateDirectoryRequest,
	FileSystemCreateDirectoryResult,
	FileSystemDeleteFileRequest,
	FileSystemDeleteFileResult,
	FileSystemDownloadFileRequest,
	FileSystemDownloadFileResult,
	FileSystemExistsRequest,
	FileSystemExistsResult,
	FileSystemExtractFileRequest,
	FileSystemExtractFileResult,
	FileSystemGetFileChecksumRequest,
	FileSystemGetFileChecksumResult,
	FileSystemGetFileRequest,
	FileSystemGetFileResult,
	FileSystemIsDirectoryRequest,
	FileSystemIsDirectoryResult,
	FileSystemLinkRequest,
	FileSystemLinkResult,
	FileSystemListFilesRequest,
	FileSystemListFilesResult,
	FileSystemListOfInternalStorageUnitsRequest,
	FileSystemListOfInternalStorageUnitsResult,
	FileSystemListOfStorageUnitsRequest,
	FileSystemListOfStorageUnitsResult,
	FileSystemMoveFileRequest,
	FileSystemMoveFileResult,
	FileSystemReadFileRequest,
	FileSystemReadFileResult,
	FileSystemRequests,
	FileSystemWipeoutRequest,
	FileSystemWipeoutResult,
	FileSystemWriteFileRequest,
	FileSystemWriteFileResult,
} from './fileSystemCommands';

export function handleFileSystemRequests(
	onCommand: (callback: (commandEvent: ITypedCommandEvent<FileSystemRequests>) => Promise<void>) => void,
	dispatch: <TCommand extends ICommand>(command: TCommand) => Promise<void>,
	frontApplet: FrontApplet,
) {
	onCommand(async (commandEvent: ITypedCommandEvent<FileSystemRequests>) => {
		switch (commandEvent.command.type) {
			case FileSystemListOfStorageUnitsRequest:
				await dispatch<FileSystemListOfStorageUnitsResult>({
					type: FileSystemListOfStorageUnitsResult,
					storageUnits: await frontApplet.fileSystem.listStorageUnits(),
				});
				break;
			case FileSystemListOfInternalStorageUnitsRequest:
				await dispatch<FileSystemListOfInternalStorageUnitsResult>({
					type: FileSystemListOfInternalStorageUnitsResult,
					storageUnits: await frontApplet.fileSystem.listInternalStorageUnits(),
				});
				break;
			case FileSystemListFilesRequest:
				await dispatch<FileSystemListFilesResult>({
					type: FileSystemListFilesResult,
					pathList: await frontApplet.fileSystem.listFiles(commandEvent.command.filePath),
				});
				break;
			case FileSystemGetFileRequest:
				await dispatch<FileSystemGetFileResult>({
					type: FileSystemGetFileResult,
					file: await frontApplet.fileSystem.getFile(commandEvent.command.filePath),
				});
				break;
			case FileSystemExistsRequest:
				await dispatch<FileSystemExistsResult>({
					type: FileSystemExistsResult,
					result: await frontApplet.fileSystem.exists(commandEvent.command.filePath),
				});
				break;
			case FileSystemDownloadFileRequest:
				await frontApplet.fileSystem.downloadFile(
					commandEvent.command.filePath,
					commandEvent.command.sourceUri,
					commandEvent.command.headers,
				);
				await dispatch<FileSystemDownloadFileResult>({
					type: FileSystemDownloadFileResult,
					result: {},
				});
				break;
			case FileSystemDeleteFileRequest:
				await frontApplet.fileSystem.deleteFile(commandEvent.command.filePath, commandEvent.command.recursive);
				await dispatch<FileSystemDeleteFileResult>({
					type: FileSystemDeleteFileResult,
					result: {},
				});
				break;
			case FileSystemMoveFileRequest:
				await frontApplet.fileSystem.moveFile(
					commandEvent.command.sourceFilePath,
					commandEvent.command.destinationFilePath,
					commandEvent.command.options,
				);
				await dispatch<FileSystemMoveFileResult>({
					type: FileSystemMoveFileResult,
					result: {},
				});
				break;
			case FileSystemGetFileChecksumRequest:
				await dispatch<FileSystemGetFileChecksumResult>({
					type: FileSystemGetFileChecksumResult,
					result: await frontApplet.fileSystem.getFileChecksum(commandEvent.command.filePath, commandEvent.command.hashType),
				});
				break;
			case FileSystemExtractFileRequest:
				await frontApplet.fileSystem.extractFile(
					commandEvent.command.archiveFilePath,
					commandEvent.command.destinationDirectionPath,
					commandEvent.command.method,
				);
				await dispatch<FileSystemExtractFileResult>({
					type: FileSystemExtractFileResult,
					result: {},
				});
				break;
			case FileSystemCreateDirectoryRequest:
				await frontApplet.fileSystem.createDirectory(commandEvent.command.directoryPath);
				await dispatch<FileSystemCreateDirectoryResult>({
					type: FileSystemCreateDirectoryResult,
					result: {},
				});
				break;
			case FileSystemCreateArchiveFileRequest:
				await frontApplet.fileSystem.createArchive(commandEvent.command.archiveFilePath, commandEvent.command.archiveEntries);
				await dispatch<FileSystemCreateArchiveFileResult>({
					type: FileSystemCreateArchiveFileResult,
					result: {},
				});
				break;
			case FileSystemIsDirectoryRequest:
				await dispatch<FileSystemIsDirectoryResult>({
					type: FileSystemIsDirectoryResult,
					result: await frontApplet.fileSystem.isDirectory(commandEvent.command.filePath),
				});
				break;
			case FileSystemCopyFileRequest:
				await frontApplet.fileSystem.copyFile(
					commandEvent.command.sourceFilePath,
					commandEvent.command.destinationFilePath,
					commandEvent.command.options,
				);
				await dispatch<FileSystemCopyFileResult>({
					type: FileSystemCopyFileResult,
					result: {},
				});
				break;
			case FileSystemWriteFileRequest:
				await frontApplet.fileSystem.writeFile(commandEvent.command.filePath, commandEvent.command.content);
				await dispatch<FileSystemWriteFileResult>({
					type: FileSystemWriteFileResult,
					result: {},
				});
				break;
			case FileSystemAppendFileRequest:
				await frontApplet.fileSystem.appendFile(commandEvent.command.filePath, commandEvent.command.content);
				await dispatch<FileSystemAppendFileResult>({
					type: FileSystemAppendFileResult,
					result: {},
				});
				break;
			case FileSystemReadFileRequest:
				await dispatch<FileSystemReadFileResult>({
					type: FileSystemReadFileResult,
					data: await frontApplet.fileSystem.readFile(commandEvent.command.filePath),
				});
				break;
			case FileSystemWipeoutRequest:
				await dispatch<FileSystemWipeoutResult>({
					type: FileSystemWipeoutResult,
					result: {},
				});
				// Perform wipeout after dispatch to ensure the command result is sent back
				await frontApplet.fileSystem.wipeout();
				break;
			case FileSystemLinkRequest:
				await frontApplet.fileSystem.link(commandEvent.command.sourceFilePath, commandEvent.command.destinationFilePath);
				await dispatch<FileSystemLinkResult>({
					type: FileSystemLinkResult,
					result: {},
				});
				break;
			default:
		}
	});
}
